import { useEffect, useMemo, useState } from 'react';
import { ApartmentOutlined, CloseOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import { ConfigProvider, Radio, Tree } from 'antd';
import type { TreeProps } from 'antd';

import { Button, Dialog, Empty, Input, Skeleton, Spinner } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { dungCay, tenDayDu } from '../../co-cau-to-chuc/nghiep-vu/cay';
import type { Department } from '../../co-cau-to-chuc/types';
import { CanhBaoTrongHop, LoiTrongHop } from '../../tenant/components/phan-form';
import { useDatQuyenDoc, usePhongBan, useQuyenDoc, useTaiNguoiPhongBan } from '../hooks/useTriThuc';
import {
  boTichNguoi,
  boTichPhong,
  canTaiPhong,
  docKhoa,
  dungNutCay,
  khoaNguoi,
  khoaPhong,
  nhanhMoSan,
  tichNguoi,
  tichPhong,
  trangThaiCay,
} from '../nghiep-vu/quyen-doc';
import type { LuaChonQuyen, NutQuyen, PhongCuaNguoi } from '../nghiep-vu/quyen-doc';
import type { DoiTuongQuyen, NguoiDanhBa, NguoiDuocDoc, PhongBan, PhongBanRef, ReadAccess } from '../types';
import { demQuyen } from './o-tep';

/** Thân hộp khi chọn riêng: cao cố định để hai cột tự cuộn trong cột, chừa chỗ cho tiêu đề, nút lưu và cảnh báo ở chân hộp. */
const THAN_CHON_RIENG = { height: 'min(75vh, calc(100vh - 240px))', display: 'flex', flexDirection: 'column', overflow: 'hidden' } as const;
const THAN_KE_THUA = { maxHeight: '75vh', overflowY: 'auto' } as const;

/** Hàng cây đủ cao để bấm; chỉ áp cho cây trong hộp này. */
const CHU_DE_CAY = { components: { Tree: { titleHeight: 32 } } };

const RONG: LuaChonQuyen = { phongBan: new Set(), nguoi: new Set() };

const demConRieng = (t: ReturnType<typeof useT>['t'], locale: string, con: NonNullable<ReadAccess['custom_children']>) => [
  ...(con.files === 0 ? [] : [con.files === 1 ? t('triThuc.quyen.motTep') : t('triThuc.quyen.soTep', { so: soVN(con.files, locale) })]),
  ...(con.folders === 0
    ? []
    : [con.folders === 1 ? t('triThuc.quyen.motThuMucCon') : t('triThuc.quyen.soThuMucCon', { so: soVN(con.folders, locale) })]),
].join(', ');

/**
 * Quyền đọc của một tệp hay thư mục: theo tầng trên hoặc chọn riêng trên một cây phòng ban và người. Bản nháp `null` khi chưa
 * đổi gì, lúc đó giá trị suy thẳng từ dữ liệu đọc về, nên không cần đồng bộ bằng `useEffect`.
 */
export function HopQuyenDoc({ kbId, doiTuong, open, onClose }: { kbId: string; doiTuong: DoiTuongQuyen; open: boolean; onClose: () => void }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const q = useQuyenDoc(kbId, doiTuong, open);
  const dat = useDatQuyenDoc(kbId);
  const ten = doiTuong.loai === 'tep' ? doiTuong.tep.file_name : doiTuong.thuMuc.name;

  const [rieng, setRieng] = useState<boolean | null>(null);
  const [banNhap, setBanNhap] = useState<LuaChonQuyen | null>(null);
  const [loiLuu, setLoiLuu] = useState<unknown>(null);
  const [nguoiDaTai, setNguoiDaTai] = useState<ReadonlyMap<string, NguoiDanhBa[]>>(new Map());
  const [loiTai, setLoiTai] = useState<ReadonlyMap<string, unknown>>(new Map());
  const [dangTai, setDangTai] = useState<ReadonlySet<string>>(new Set());
  const [dangDoi, setDangDoi] = useState(false);
  const taiNguoi = useTaiNguoiPhongBan();

  const banDau = useMemo<LuaChonQuyen>(
    () => (q.data ? { phongBan: new Set(q.data.departments.map((p) => p.id)), nguoi: new Set(q.data.users.map((u) => u.user_id)) } : RONG),
    [q.data],
  );
  const chon = banNhap ?? banDau;
  const dangRieng = rieng ?? (q.data ? !q.data.inherit : false);

  const doiChon = (lam: (cu: LuaChonQuyen) => LuaChonQuyen) => {
    setBanNhap((cu) => lam(cu ?? banDau));
    setLoiLuu(null);
  };

  /** Tải người một phòng vào cây. Lỗi nằm cạnh cây; trả `null` để nơi gọi không đổi lựa chọn. */
  const taiPhong = async (phong: string): Promise<NguoiDanhBa[] | null> => {
    setLoiTai((cu) => {
      const moi = new Map(cu);
      moi.delete(phong);
      return moi;
    });
    setDangTai((cu) => new Set(cu).add(phong));
    try {
      const ds = await taiNguoi(phong);
      setNguoiDaTai((cu) => new Map(cu).set(phong, ds));
      return ds;
    } catch (err) {
      setLoiTai((cu) => new Map(cu).set(phong, err));
      return null;
    } finally {
      setDangTai((cu) => {
        const moi = new Set(cu);
        moi.delete(phong);
        return moi;
      });
    }
  };

  const luu = async () => {
    setLoiLuu(null);
    try {
      await dat.mutateAsync({
        doiTuong,
        du: dangRieng
          ? { inherit: false, department_ids: [...chon.phongBan], user_ids: [...chon.nguoi] }
          : { inherit: true, department_ids: [], user_ids: [] },
      });
      onClose();
    } catch (err) {
      setLoiLuu(err);
    }
  };

  const con = q.data?.custom_children;
  const coCon = con && con.files + con.folders > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('triThuc.quyen.tieuDe', { ten })}
      width="min(1000px, calc(100vw - 32px))"
      centered
      styles={{ body: q.data && dangRieng ? THAN_CHON_RIENG : THAN_KE_THUA }}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {q.data && dangRieng && chon.phongBan.size === 0 && chon.nguoi.size === 0 && (
            <CanhBaoTrongHop>{t('triThuc.quyen.canhBaoRong')}</CanhBaoTrongHop>
          )}
          {coCon && <p className="text-left text-xs text-content-muted">{t('triThuc.quyen.conRieng', { ds: demConRieng(t, locale, con) })}</p>}
          {Boolean(loiLuu) && <LoiTrongHop>{dichLoi(loiLuu)}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" loading={dat.isPending} disabled={!q.data || dangDoi} onClick={() => void luu()}>
              {dat.isPending ? t('chung.dangLuu') : t('triThuc.quyen.luu')}
            </Button>
          </div>
        </div>
      }
    >
      {q.isLoading ? (
        <div className="space-y-3 pt-2" aria-busy>
          <Skeleton className="h-5 w-1/2" />
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : q.isError || !q.data ? (
        <div className="space-y-3 pt-2">
          <LoiTrongHop>{t('triThuc.quyen.loiDoc', { loi: dichLoi(q.error) })}</LoiTrongHop>
          <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
            {t('chung.thuLai')}
          </Button>
        </div>
      ) : (
        <NoiDung
          duLieu={q.data}
          rieng={dangRieng}
          onDoiKieu={(v) => {
            setRieng(v);
            setLoiLuu(null);
          }}
          chon={chon}
          doiChon={doiChon}
          nguoiDaTai={nguoiDaTai}
          loiTai={loiTai}
          dangTai={dangTai}
          taiPhong={taiPhong}
          dangDoi={dangDoi}
          setDangDoi={setDangDoi}
        />
      )}
    </Dialog>
  );
}

interface NoiDungProps {
  duLieu: ReadAccess;
  rieng: boolean;
  onDoiKieu: (rieng: boolean) => void;
  chon: LuaChonQuyen;
  doiChon: (lam: (cu: LuaChonQuyen) => LuaChonQuyen) => void;
  nguoiDaTai: ReadonlyMap<string, NguoiDanhBa[]>;
  loiTai: ReadonlyMap<string, unknown>;
  dangTai: ReadonlySet<string>;
  taiPhong: (phong: string) => Promise<NguoiDanhBa[] | null>;
  dangDoi: boolean;
  setDangDoi: (v: boolean) => void;
}

function NoiDung({ duLieu, rieng, onDoiKieu, ...phan }: NoiDungProps) {
  const { t } = useT();
  const { parent, source } = duLieu.inherited;
  const nhanKeThua = parent?.type === 'FOLDER'
    ? (parent.name ? t('triThuc.quyen.theoThuMuc', { ten: parent.name }) : t('triThuc.quyen.theoThuMucKhongTen'))
    : t('triThuc.quyen.theoBoTriThuc');
  // Tầng trên trực tiếp cũng đang kế thừa thì nói rõ quyền thực sự đến từ đâu.
  const nguonXa = source?.type === 'FOLDER' && source.name && source.id !== parent?.id ? source.name : null;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 pt-2">
      <Radio.Group
        className="flex shrink-0 flex-col gap-2"
        aria-label={t('triThuc.quyen.kieu')}
        value={rieng ? 'rieng' : 'ke-thua'}
        onChange={(e) => onDoiKieu(e.target.value === 'rieng')}
      >
        <Radio value="ke-thua">
          <span className="break-words text-sm font-medium text-content-primary">{nhanKeThua}</span>
        </Radio>
        {!rieng && (
          <KhoiKeThua
            nguonXa={nguonXa}
            phongBan={duLieu.inherited.departments}
            nguoi={duLieu.inherited.users}
          />
        )}
        <Radio value="rieng">
          <span className="text-sm font-medium text-content-primary">{t('triThuc.quyen.chonRieng')}</span>
        </Radio>
      </Radio.Group>
      {rieng && <KhoiChonRieng nguoiBanDau={duLieu.users} tenBanDau={duLieu.departments} {...phan} />}
    </div>
  );
}

/** Phần chỉ đọc khi kế thừa: phòng ban và người đang có hiệu lực. */
function KhoiKeThua({ nguonXa, phongBan, nguoi }: { nguonXa: string | null; phongBan: PhongBanRef[]; nguoi: NguoiDuocDoc[] }) {
  const { t } = useT();
  const dsPhong = usePhongBan();
  const tenPhong = new Map((dsPhong.data?.items ?? []).map((p) => [p.id, p.name]));
  const khongRoNguoi = t('nktv.nguoi.khongRoTen');
  const khongRoPhong = t('triThuc.quyen.phongBanKhongRo');
  return (
    <div className="ml-6 space-y-2 rounded border border-default bg-subtle px-3 py-2.5 text-xs">
      {nguonXa && <p className="break-words text-content-muted">{t('triThuc.quyen.layTuThuMuc', { ten: nguonXa })}</p>}
      {phongBan.length === 0 && nguoi.length === 0 ? (
        <p className="font-medium text-warning-700">{t('triThuc.quyen.khongAiDoc')}</p>
      ) : (
        <dl className="space-y-1.5">
          {phongBan.length > 0 && (
            <div className="flex gap-2">
              <dt className="w-20 shrink-0 text-content-muted">{t('triThuc.quyen.fPhongBan')}</dt>
              <dd className="min-w-0 break-words text-content-primary">
                {phongBan.map((p) => p.name ?? tenPhong.get(p.id) ?? khongRoPhong).join(', ')}
              </dd>
            </div>
          )}
          {nguoi.length > 0 && (
            <div className="flex gap-2">
              <dt className="w-20 shrink-0 text-content-muted">{t('triThuc.quyen.fNguoi')}</dt>
              <dd className="min-w-0 break-words text-content-primary">
                {nguoi.map((u) => u.display_name?.trim() || khongRoNguoi).join(', ')}
              </dd>
            </div>
          )}
        </dl>
      )}
    </div>
  );
}

type PhanChonRieng = Omit<NoiDungProps, 'duLieu' | 'rieng' | 'onDoiKieu'> & { nguoiBanDau: NguoiDuocDoc[]; tenBanDau: PhongBanRef[] };

/** Chọn riêng: đủ bốn trạng thái của danh bạ, rồi mới dựng cây để nhánh mở sẵn tính đúng một lần. */
function KhoiChonRieng(p: PhanChonRieng) {
  const { t } = useT();
  const dichLoi = useLoi();
  const dsPhong = usePhongBan();
  if (dsPhong.isLoading) {
    return (
      <div className="space-y-2" aria-busy>
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-5 w-1/2" />
      </div>
    );
  }
  if (dsPhong.isError || !dsPhong.data) {
    return (
      <div className="space-y-2">
        <LoiTrongHop>{t('triThuc.loi.phongBan', { loi: dichLoi(dsPhong.error) })}</LoiTrongHop>
        <Button variant="outline" size="sm" onClick={() => void dsPhong.refetch()}>
          {t('chung.thuLai')}
        </Button>
      </div>
    );
  }
  return <CayVaDaChon dsPhong={dsPhong.data.items} {...p} />;
}

function CayVaDaChon({
  dsPhong, nguoiBanDau, tenBanDau, chon, doiChon, nguoiDaTai, loiTai, dangTai, taiPhong, dangDoi, setDangDoi,
}: PhanChonRieng & { dsPhong: PhongBan[] }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const [tim, setTim] = useState('');

  const phongCua = useMemo<PhongCuaNguoi>(() => {
    const m = new Map<string, string | null>(nguoiBanDau.map((u) => [u.user_id, u.department_id]));
    for (const ds of nguoiDaTai.values()) for (const u of ds) m.set(u.user_id, u.department_id);
    return m;
  }, [nguoiBanDau, nguoiDaTai]);

  const [moRong, setMoRong] = useState<string[]>(() => nhanhMoSan(dsPhong, chon, phongCua));

  // `loadData` của `Tree` không gọi cho nút đã có con, mà phòng ban có phòng con vẫn có người riêng: tự tải khi mở nhánh.
  const moNhanh = (khoa: string) => {
    const { id } = docKhoa(khoa);
    if (!nguoiDaTai.has(id)) void taiPhong(id);
  };
  useEffect(() => {
    // Nhánh mở sẵn lúc vào hộp, chỉ một lần; tải trùng do StrictMode đã được cache của query gộp lại.
    moRong.forEach(moNhanh);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dayDu = useMemo<Department[]>(() => dsPhong.map((d) => ({ created_at: '', user_count: 0, child_count: 0, ...d })), [dsPhong]);
  const cayPhong = useMemo(() => dungCay(dayDu, locale), [dayDu, locale]);
  const { nut, moRong: moKhiTim } = useMemo(() => dungNutCay(cayPhong, nguoiDaTai, tim, locale), [cayPhong, nguoiDaTai, tim, locale]);
  const dangTim = tim.trim().length > 0;

  const khongRoNguoi = t('nktv.nguoi.khongRoTen');
  const tenPhong = (id: string) =>
    tenDayDu(dayDu, id) ?? tenBanDau.find((d) => d.id === id)?.name ?? t('triThuc.quyen.phongBanKhongRo');
  const tenNgan = (id: string) =>
    dsPhong.find((d) => d.id === id)?.name ?? tenBanDau.find((d) => d.id === id)?.name ?? t('triThuc.quyen.phongBanKhongRo');

  const boPhong = (id: string) => doiChon((c) => boTichPhong(dsPhong, c, id, phongCua));

  const boNguoi = async (id: string) => {
    const phong = phongCua.get(id);
    if (!canTaiPhong(chon, phong)) {
      doiChon((c) => boTichNguoi(c, id, phong, []));
      return;
    }
    setDangDoi(true);
    const ds = await taiPhong(phong);
    setDangDoi(false);
    if (ds) doiChon((c) => boTichNguoi(c, id, phong, ds.map((u) => u.user_id)));
  };

  const onCheck: TreeProps['onCheck'] = (_v, info) => {
    if (dangDoi) return;
    const { loai, id } = docKhoa(String(info.node.key));
    if (loai === 'phong') {
      if (info.checked) doiChon((c) => tichPhong(dsPhong, c, id, phongCua));
      else boPhong(id);
    } else if (info.checked) {
      doiChon((c) => tichNguoi(c, id));
    } else {
      void boNguoi(id);
    }
  };

  const veNut = (ds: NutQuyen[]): NonNullable<TreeProps['treeData']> =>
    ds.map((n) => {
      const ten = n.ten?.trim() || khongRoNguoi;
      const Icon = n.loai === 'phong' ? ApartmentOutlined : UserOutlined;
      return {
        key: n.loai === 'phong' ? khoaPhong(n.id) : khoaNguoi(n.id),
        isLeaf: n.la,
        title: (
          <span className="inline-flex min-w-0 items-baseline gap-1.5 text-sm text-content-primary">
            <Icon aria-hidden className="shrink-0 text-xs text-content-muted" />
            <span className="min-w-0 break-words">{ten}</span>
          </span>
        ),
        children: dangTai.has(n.id)
          ? [...veNut(n.con), {
            key: `tai:${n.id}`,
            checkable: false,
            isLeaf: true,
            title: (
              <span role="status" className="inline-flex items-center gap-2 text-xs text-content-muted">
                <Spinner size="small" />
                {t('triThuc.quyen.dangTai')}
              </span>
            ),
          }]
          : veNut(n.con),
      };
    });

  const dsNguoiChon = useMemo(() => {
    const theoId = new Map<string, { ten: string | null; phong: string | null }>();
    for (const u of nguoiBanDau) theoId.set(u.user_id, { ten: u.display_name, phong: u.department_id });
    for (const ds of nguoiDaTai.values()) for (const u of ds) theoId.set(u.user_id, { ten: u.display_name, phong: u.department_id });
    return [...chon.nguoi].map((id) => ({ id, ten: theoId.get(id)?.ten ?? null, phong: theoId.get(id)?.phong ?? null }))
      .sort((a, b) => (a.ten === null ? 1 : 0) - (b.ten === null ? 1 : 0) || (a.ten ?? '').localeCompare(b.ten ?? '', locale));
  }, [chon.nguoi, locale, nguoiBanDau, nguoiDaTai]);

  const thuTuPhong = cayPhong.length > 0 ? [...chon.phongBan].sort((a, b) => tenPhong(a).localeCompare(tenPhong(b), locale)) : [...chon.phongBan];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 lg:grid lg:grid-cols-5 lg:grid-rows-1">
      <div className="flex min-h-0 min-w-0 flex-[3] flex-col gap-2 lg:col-span-3">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-content-muted" />}
          placeholder={t('triThuc.quyen.tim')}
          aria-label={t('triThuc.quyen.tim')}
          value={tim}
          onChange={(e) => setTim(e.target.value)}
        />
        {[...loiTai].map(([phong, loi]) => (
          <div key={phong} role="alert" className="flex flex-wrap items-center justify-between gap-2">
            <span className="min-w-0 text-xs text-danger-700">{t('triThuc.quyen.loiTaiPhong', { phong: tenNgan(phong), loi: dichLoi(loi) })}</span>
            <Button variant="outline" size="sm" onClick={() => void taiPhong(phong)}>
              {t('chung.thuLai')}
            </Button>
          </div>
        ))}
        {dangDoi && (
          <p role="status" className="flex items-center gap-2 text-xs text-content-muted">
            <Spinner size="small" />
            {t('triThuc.quyen.dangTaiPhong')}
          </p>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto rounded border border-default p-2" role="group" aria-label={t('triThuc.quyen.cay')}>
          {dsPhong.length === 0 ? (
            <Empty icon={ApartmentOutlined} title={t('triThuc.quyen.chuaCoPhongBan')} />
          ) : nut.length === 0 ? (
            <Empty icon={SearchOutlined} title={t('triThuc.quyen.rongTim')} />
          ) : (
            <ConfigProvider theme={CHU_DE_CAY}>
              <Tree
                checkable
                checkStrictly
                blockNode
                selectable={false}
                treeData={veNut(nut)}
                checkedKeys={trangThaiCay(dsPhong, chon, phongCua, nguoiDaTai)}
                onCheck={onCheck}
                expandedKeys={dangTim ? moKhiTim : moRong}
                onExpand={(k, info) => {
                  if (dangTim) return;
                  setMoRong(k.map(String));
                  if (info.expanded) moNhanh(String(info.node.key));
                }}
              />
            </ConfigProvider>
          )}
        </div>
      </div>

      <section className="flex min-h-0 min-w-0 flex-[2] flex-col rounded border border-default lg:col-span-2" aria-label={t('triThuc.quyen.daChon')}>
        <div className="flex shrink-0 flex-wrap items-baseline justify-between gap-x-2 border-b border-default px-3 py-2">
          <h3 className="text-sm font-medium text-content-primary">{t('triThuc.quyen.daChon')}</h3>
          <span className="text-xs tabular-nums text-content-muted">{demQuyen(t, locale, chon.phongBan.size, chon.nguoi.size)}</span>
        </div>
        {chon.phongBan.size === 0 && chon.nguoi.size === 0 ? (
          <p className="px-3 py-3 text-xs text-content-muted">{t('triThuc.quyen.chuaChonAi')}</p>
        ) : (
          <ul className="min-h-0 flex-1 divide-y divide-default overflow-y-auto">
            {thuTuPhong.map((id) => (
              <MucDaChon key={id} loai="phong" ten={tenNgan(id)} phu={tenPhong(id) === tenNgan(id) ? undefined : tenPhong(id)} onBo={() => boPhong(id)} />
            ))}
            {dsNguoiChon.map((u) => (
              <MucDaChon
                key={u.id}
                loai="nguoi"
                ten={u.ten?.trim() || khongRoNguoi}
                phu={u.phong ? tenNgan(u.phong) : t('triThuc.quyen.khongThuocPhong')}
                voHieu={dangDoi}
                onBo={() => void boNguoi(u.id)}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function MucDaChon({ loai, ten, phu, voHieu, onBo }: { loai: 'phong' | 'nguoi'; ten: string; phu?: string | undefined; voHieu?: boolean; onBo: () => void }) {
  const { t } = useT();
  const Icon = loai === 'phong' ? ApartmentOutlined : UserOutlined;
  const nhan = t('triThuc.quyen.bo', { ten });
  return (
    <li className="flex items-start gap-2 px-3 py-2">
      <Icon aria-hidden className="mt-1 shrink-0 text-xs text-content-muted" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="break-words text-sm text-content-primary">{ten}</span>
        {phu && <span className="break-words text-xs text-content-muted">{phu}</span>}
      </span>
      <button
        type="button"
        disabled={voHieu}
        className="shrink-0 rounded p-1 text-content-muted hover:bg-subtle hover:text-danger-700 disabled:opacity-50"
        title={nhan}
        aria-label={nhan}
        onClick={onBo}
      >
        <CloseOutlined aria-hidden className="text-2xs" />
      </button>
    </li>
  );
}
